import { useEffect, useState } from 'react'
import { Document, Image, Page, Path, StyleSheet, Svg, Text, View, pdf } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import logo from 'src/logo.svg'
import VITlogo from 'src/VITlogo.png'
import { quoteLineType, quoteType } from 'src/types'
import {
  AddressDetails,
  ContactDetails,
  LINE_HEADERS,
  PreviewRow,
  PreviewRowKind,
  addressLines,
  buildLineTables,
  signatureBlock,
} from './quotePdfModel'
import { formatCurrency } from 'src/helper'

const HEAD = '#20385b'
const BUNDLE = '#c7d1df'
const ALT = '#f4f6f9'
const BUNDLE_TEXT = '#1a2d4a'
const GRAND = '#ffde59'
const BORDER = '#8899aa'
const BOLD = 'Helvetica-Bold'

const LINE_COLUMNS: { width: number; align: 'left' | 'right' | 'center'; breakAnywhere?: boolean }[] = [
  { width: 34, align: 'right' },
  { width: 34, align: 'right' },
  { width: 88, align: 'left', breakAnywhere: true },
  { width: 88, align: 'left', breakAnywhere: true },
  { width: 88, align: 'left', breakAnywhere: true },
  { width: 218, align: 'left', breakAnywhere: true },
  { width: 34, align: 'center' },
  { width: 68, align: 'right' },
  { width: 68, align: 'right' },
]
const TABLE_WIDTH = LINE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const FOOTER_HEIGHT = 32

const s = StyleSheet.create({
  page: {
    padding: 36,
    paddingBottom: 52 + FOOTER_HEIGHT,
    fontFamily: 'Helvetica',
    fontSize: 8,
    color: '#000',
    gap: 14,
  },
  headerTop: {
    position: 'absolute',
    top: 36,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 18,
    letterSpacing: 4.5,
    color: HEAD,
    fontFamily: BOLD,
  },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 },
  headerLeft: { gap: 8 },
  logoVit: { width: 220, marginTop: -7 },
  companyAddress: { fontSize: 9, lineHeight: 1.35 },
  headerRight: { alignItems: 'flex-end', gap: 18, minWidth: 220 },
  meta: { gap: 2, fontSize: 9 },
  metaRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 4 },
  bold: { fontFamily: BOLD },
  grandHeadline: { fontSize: 13, fontFamily: BOLD, textAlign: 'right' },
  row: { flexDirection: 'row' },
  th: { backgroundColor: HEAD, color: '#fff', fontFamily: BOLD, fontSize: 8, padding: '3 4' },
  addressCol: { flex: 1 },
  addressGap: { marginLeft: 60 },
  lineRow: { flexDirection: 'row', borderLeftWidth: 0.5, borderColor: BORDER },
  tableTop: { borderTopWidth: 0.5 },
  tableStart: { borderTopWidth: 0.5, marginTop: 14 },
  // lineHeight multipliers resolve against this node's own fontSize, so keep them paired.
  addressCell: { padding: '3 4', fontSize: 8, lineHeight: 1.35 },
  cell: { padding: '3 4', borderRightWidth: 0.5, borderBottomWidth: 0.5, borderColor: BORDER },
  closing: { gap: 14 },
  totalsBlock: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 },
  comments: { width: 330, borderLeftWidth: 0.5, borderTopWidth: 0.5, borderColor: BORDER },
  commentsCell: { minHeight: 60 },
  totals: { gap: 4, fontSize: 9, fontFamily: BOLD, minWidth: 180, marginLeft: 'auto' },
  totalsRow: { flexDirection: 'row', justifyContent: 'flex-end', gap: 24 },
  totalsValue: { minWidth: 72, textAlign: 'right' },
  grandRow: { backgroundColor: GRAND, padding: '2 6', marginTop: 2 },
  signature: { fontSize: 9, gap: 14 },
  signatureCols: { flexDirection: 'row', gap: 12, fontSize: 9, lineHeight: 1.5 },
  signatureLeft: { width: 184 },
  footer: {
    position: 'absolute',
    left: 36,
    right: 36,
    bottom: 52,
    textAlign: 'center',
    fontSize: 7,
    lineHeight: 1.4,
  },
  pageNumber: { position: 'absolute', right: 36, bottom: 18, fontSize: 8, textAlign: 'right' },
})

const ROW_STYLES: Record<PreviewRowKind, { backgroundColor?: string; color?: string; fontFamily?: string }> = {
  item: {},
  bundleItem: {},
  bundle: { backgroundColor: BUNDLE, color: BUNDLE_TEXT, fontFamily: BOLD },
  groupTotal: { backgroundColor: BUNDLE, color: BUNDLE_TEXT, fontFamily: BOLD },
}

// react-pdf only wraps at spaces and English syllables, so part numbers otherwise overflow into the next column.
const breakAnywhere = (word: string) => Array.from(word)

type LogoPath = { d: string; fill?: string; fillRule?: string }

let logoPathsPromise: Promise<LogoPath[]> | null = null

function loadLogoPaths(): Promise<LogoPath[]> {
  logoPathsPromise ??= fetch(logo)
    .then(res => res.text())
    .then(text =>
      [...new DOMParser().parseFromString(text, 'image/svg+xml').querySelectorAll('path')].map(path => ({
        d: path.getAttribute('d') ?? '',
        fill: path.getAttribute('fill') ?? undefined,
        fillRule: path.getAttribute('fill-rule') ?? undefined,
      })),
    )
    .catch(err => {
      logoPathsPromise = null
      throw err
    })
  return logoPathsPromise
}

function ImpresLogo({ paths }: { paths: LogoPath[] }) {
  // Crops the square 102x102 artwork to 189x90pt at 65% vertical, matching the old object-fit: cover.
  return (
    <Svg width={189} height={90} viewBox="0 34.73 102 48.57">
      {paths.map((path, i) => (
        <Path key={i} d={path.d} fill={path.fill} fillRule={path.fillRule as 'nonzero' | 'evenodd' | undefined} />
      ))}
    </Svg>
  )
}

function QuotePdfPageHeader({
  quote,
  isBVit,
  logoPaths,
}: {
  quote: quoteType
  isBVit: boolean
  logoPaths: LogoPath[]
}) {
  const meta: [string, string][] = [
    ['Quote #: ', quote.QuoteNumber],
    ['Date: ', quote.QuoteDate ? dayjs(quote.QuoteDate).format('MM/DD/YYYY') : ''],
    ['Expires: ', quote.ExpirationDate ? dayjs(quote.ExpirationDate).format('MM/DD/YYYY') : ''],
    ['Contract: ', quote.ContractVehicle ?? ''],
    ['Contract No.: ', quote.ContractNumber ?? ''],
  ]
  return (
    <View style={s.header} fixed>
      <View style={s.headerLeft}>
        {isBVit ? <Image src={VITlogo} style={s.logoVit} /> : <ImpresLogo paths={logoPaths} />}
        <View style={s.companyAddress}>
          {isBVit ? (
            <>
              <Text>5475 Mark Dabling Blvd., Suite 320</Text>
              <Text>Colorado Springs, CO 80918</Text>
            </>
          ) : (
            <>
              <Text>810 Hesters Crossing Rd., Suite 100</Text>
              <Text>Round Rock, TX 78681</Text>
            </>
          )}
        </View>
      </View>
      <View style={s.headerRight}>
        <View style={s.meta}>
          {meta.map(([label, value]) => (
            <View key={label} style={s.metaRow}>
              <Text style={s.bold}>{label}</Text>
              <Text>{value}</Text>
            </View>
          ))}
        </View>
        <Text
          style={s.grandHeadline}
          render={({ pageNumber }) => (pageNumber === 1 ? `QUOTED TOTAL: ${formatCurrency(quote.GrandTotal)}` : '')}
        />
      </View>
    </View>
  )
}

function QuotePdfAddressBlock({ soldTo, shipTo }: { soldTo: string[]; shipTo: string[] }) {
  return (
    <View style={s.row}>
      {[
        ['SOLD TO', soldTo],
        ['SHIP TO', shipTo],
      ].map(([title, lines], i) => (
        <View key={title as string} style={i === 1 ? [s.addressCol, s.addressGap] : s.addressCol}>
          <Text style={s.th}>{title}</Text>
          <View style={s.addressCell}>
            {(lines as string[]).map((line, j) => (
              <Text key={j}>{line}</Text>
            ))}
          </View>
        </View>
      ))}
    </View>
  )
}

function QuotePdfLineTables({ tables }: { tables: PreviewRow[][] }) {
  return (
    <View>
      <View style={[s.lineRow, s.tableTop]} fixed>
        {LINE_HEADERS.map((header, i) => (
          <Text key={header} style={[s.th, s.cell, { width: LINE_COLUMNS[i].width, textAlign: LINE_COLUMNS[i].align }]}>
            {header}
          </Text>
        ))}
      </View>
      {tables.flatMap((rows, t) => {
        const firstLine = rows[0].kind === 'bundle' ? 1 : 0
        return rows.map((row, index) => (
          <View
            key={`${t}-${index}`}
            wrap={false}
            minPresenceAhead={row.kind === 'bundle' ? 30 : undefined}
            style={[
              s.lineRow,
              ROW_STYLES[row.kind],
              (row.kind === 'item' || row.kind === 'bundleItem') && (index - firstLine) % 2 === 1
                ? { backgroundColor: ALT }
                : {},
              t > 0 && index === 0 ? s.tableStart : {},
            ]}
          >
            {row.kind === 'bundle' ? (
              // eslint-disable-next-line no-restricted-syntax
              <Text style={[s.cell, { width: TABLE_WIDTH, textAlign: 'center' }]}>{row.cells[0]}</Text>
            ) : (
              row.cells.map((cell, i) => (
                <Text
                  key={i}
                  hyphenationCallback={LINE_COLUMNS[i].breakAnywhere ? breakAnywhere : undefined}
                  style={[s.cell, { width: LINE_COLUMNS[i].width, textAlign: LINE_COLUMNS[i].align }]}
                >
                  {cell}
                </Text>
              ))
            )}
          </View>
        ))
      })}
    </View>
  )
}

function QuotePdfTotalsBlock({ quote }: { quote: quoteType }) {
  const rows: [string, number][] = [
    ['Subtotal', quote.Subtotal],
    ['Tax', quote.Tax],
    ['Shipping', quote.ShippingHandling],
  ]
  return (
    <View style={s.totalsBlock}>
      <View style={s.comments}>
        <Text style={[s.th, s.cell]}>COMMENTS</Text>
        <Text style={[s.cell, s.commentsCell]}>{quote.Comments ?? ''}</Text>
      </View>
      <View style={s.totals}>
        {rows.map(([label, value]) => (
          <View key={label} style={s.totalsRow}>
            <Text>{label}</Text>
            <Text style={s.totalsValue}>{formatCurrency(value)}</Text>
          </View>
        ))}
        <View style={[s.totalsRow, s.grandRow]}>
          <Text>GRAND TOTAL</Text>
          <Text style={s.totalsValue}>{formatCurrency(quote.GrandTotal)}</Text>
        </View>
      </View>
    </View>
  )
}

function QuotePdfSignature({ signature }: { signature: { left: string[]; right: string[] } }) {
  return (
    <View style={s.signature}>
      <Text>Respectfully,</Text>
      <View style={s.signatureCols}>
        <View style={s.signatureLeft}>
          {signature.left.map((line, i) => (
            <Text key={i}>{line}</Text>
          ))}
        </View>
        <View>
          {signature.right.map((line, i) => (
            <Text key={i}>{line}</Text>
          ))}
        </View>
      </View>
    </View>
  )
}

export type QuotePdfDocumentProps = {
  quote: quoteType
  lineItems: (quoteLineType & { children?: quoteLineType[] })[]
  soldToDetails: AddressDetails
  shippingDetails: AddressDetails
  isrDetails: ContactDetails
  aeDetails: ContactDetails
}

function QuotePdfDocument({
  quote,
  lineItems,
  soldToDetails,
  shippingDetails,
  isrDetails,
  aeDetails,
  logoPaths,
}: QuotePdfDocumentProps & { logoPaths: LogoPath[] }) {
  const isBVit = quote.ContractNumber?.toLowerCase() === 'nng15sd41b'
  const tables = buildLineTables(lineItems)
  const soldTo = addressLines(soldToDetails)
  const shipTo = addressLines(shippingDetails)
  const signature = signatureBlock(isrDetails, aeDetails)

  return (
    <Document title={quote.QuoteNumber || 'Quote'}>
      <Page size="LETTER" orientation="landscape" style={s.page}>
        <Text style={s.headerTop} fixed>
          QUOTATION
        </Text>
        <QuotePdfPageHeader quote={quote} isBVit={isBVit} logoPaths={logoPaths} />
        <QuotePdfAddressBlock soldTo={soldTo} shipTo={shipTo} />
        {tables.length > 0 && <QuotePdfLineTables tables={tables} />}
        <View style={s.closing} wrap={false}>
          <QuotePdfTotalsBlock quote={quote} />
          <QuotePdfSignature signature={signature} />
        </View>
        <View style={s.footer} fixed>
          {isBVit ? (
            <>
              <Text>UEI: JD85DN2HY8K4 • DUNS: 079129400 • CAGE CODE: 6ZQW0 • TAX ID: 46-3629538</Text>
              <Text>http://www.vetinfotech.com/</Text>
              <Text>T.562-298-4030 • F.562-298-4031</Text>
              <Text>Orders can be sent to: sewporders@sewp.nasa.gov</Text>
            </>
          ) : (
            <>
              <Text>UEI: MSSQQ551LG41 • DUNS: 011182784 • CAGE: 3UTC7 • TAX ID:95-4862840</Text>
              <Text>imprestechnology.com</Text>
              <Text>T.562-298-4030 • F.562-298-4031</Text>
            </>
          )}
        </View>
        <Text
          style={s.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
      </Page>
    </Document>
  )
}

export async function quotePdfBlob(props: QuotePdfDocumentProps): Promise<Blob> {
  const logoPaths = await loadLogoPaths()
  return pdf(<QuotePdfDocument {...props} logoPaths={logoPaths} />).toBlob()
}

export function quotePdfFileName(quote: quoteType) {
  return `${quote.QuoteNumber || 'Quote'}.pdf`
}

export async function downloadQuotePdf(props: QuotePdfDocumentProps) {
  const url = URL.createObjectURL(await quotePdfBlob(props))
  const a = document.createElement('a')
  a.href = url
  a.download = quotePdfFileName(props.quote)
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export function QuotePdfPreview({ className, ...props }: QuotePdfDocumentProps & { className?: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const propsKey = JSON.stringify(props)

  useEffect(() => {
    let cancelled = false
    let created: string | null = null
    quotePdfBlob(props)
      .then(blob => {
        if (cancelled) return
        created = URL.createObjectURL(blob)
        setUrl(created)
      })
      .catch(err => console.error(err))
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [propsKey])

  return url ? <iframe className={className} src={`${url}#toolbar=0`} title="Quote PDF preview" /> : null
}
